import { GLOBAL } from '../config/global'
import { WORD_SETS, SET_BY_WORD } from '../config/dictionary'
import { wordXpToNext } from './formulas'

// A collected word and its progression (§4.2).
export interface WordEntry {
  word: string
  level: number // starts at 1 on first craft
  xp: number // uses accumulated toward the next level
  uses: number // lifetime craft count
  mastered: boolean
  lastBurstAt: number // ms epoch of last Ink-bearing burst (per-word cooldown)
}

export function newWordEntry(word: string): WordEntry {
  return { word, level: 1, xp: 0, uses: 0, mastered: false, lastBurstAt: 0 }
}

// Apply one craft: +1 use, +1 xp, level up while xp threshold met, set mastery.
export function progressWord(e: WordEntry): WordEntry {
  let { level, xp } = e
  xp += 1
  while (level < GLOBAL.wordMaxLevel && xp >= wordXpToNext(level)) {
    xp -= wordXpToNext(level)
    level += 1
  }
  const mastered = level >= GLOBAL.wordMaxLevel
  return { ...e, level, xp, uses: e.uses + 1, mastered }
}

// Set completion bonus (§8.8): each completed set +5%, total capped at +50%.
export function setBonus(collected: Set<string>): number {
  let completed = 0
  for (const s of WORD_SETS) {
    if (s.members.every((w) => collected.has(w))) completed++
  }
  return Math.min(completed * GLOBAL.setBonusEachMax, GLOBAL.setBonusTotalMax)
}

// Mastery global bonus (§8.8): +0.5% per mastered word, capped at +25%.
export function masteryBonus(masteredCount: number): number {
  return Math.min(
    masteredCount * GLOBAL.masteryGlobalPerWord,
    GLOBAL.masteryGlobalMax,
  )
}

// Combined non-prestige global multiplier (Set + Mastery), pre-hard-cap.
// The ×3 hard cap is applied at burst time in wordBurst().
export function globalMultiplier(collected: Set<string>, masteredCount: number): number {
  return 1 + setBonus(collected) + masteryBonus(masteredCount)
}

export function completedSets(collected: Set<string>): string[] {
  return WORD_SETS.filter((s) => s.members.every((w) => collected.has(w))).map((s) => s.id)
}

export function setProgress(collected: Set<string>) {
  return WORD_SETS.map((s) => ({
    id: s.id,
    name: s.name,
    have: s.members.filter((w) => collected.has(w)).length,
    total: s.members.length,
  }))
}

export { SET_BY_WORD }
